// SingleIssuePage.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  X,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle2,
  ThumbsUp,
  Lightbulb,
  MessageSquare,
  User,
  Loader2,
  Cpu,
} from "lucide-react";
import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";
import { getTimeAgo } from "../utils/helper";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

/* --------------------------------------------------------------------------
   STYLED COMPONENTS - Purple glass theme (matching IssuesListPage)
   -------------------------------------------------------------------------- */

const Container = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 24px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  width: 100%;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const IssueCard = styled.article`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(155, 92, 255, 0.12);
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  gap: 12px;

  h3 {
    margin: 0;
    color: #fff;
    font-size: 1.1rem;
    line-height: 1.25;
  }

  p {
    margin: 0;
    color: #cfcfd6;
    font-size: 0.98rem;
    line-height: 1.5;
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AvatarWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9b5cff, #c59fff);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  span:first-child {
    font-weight: 700;
    color: #fff;
  }

  span:last-child {
    font-size: 12px;
    color: #aab1c7;
  }
`;

const Screenshot = styled.img`
  width: 100%;
  max-height: 420px;
  object-fit: cover;
  border-radius: 10px;
  margin-top: 8px;
`;

const CodeBlock = styled.pre`
  background: #0e0e0f;
  padding: 12px;
  border-radius: 8px;
  color: #e6e6e6;
  font-size: 13px;
  max-height: 300px;
  overflow: auto;
`;

const ViewMore = styled.button`
  align-self: flex-end;
  background: transparent;
  border: none;
  color: #c59fff;
  font-weight: 700;
  cursor: pointer;
  padding: 6px;
`;

const Meta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto;
`;

const Tag = styled.span`
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 12px;
  color: #fff;
  text-transform: capitalize;
  background: ${({ variant }) =>
    variant === "type"
      ? "linear-gradient(90deg,#6c5ce7,#9b5cff)"
      : variant === "tech"
      ? "linear-gradient(90deg,#00cec9,#00fcc9)"
      : variant === "difficult"
      ? "linear-gradient(90deg,#ffb86b,#ff7a7a)"
      : "rgba(255,255,255,0.04)"};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 14px;

  button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    border: none;
    background: rgba(255, 255, 255, 0.03);
    color: #d6d6df;
    cursor: pointer;
    font-weight: 700;
    transition: transform 0.12s ease, background 0.12s ease;
  }

  button.primary {
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    color: white;
  }

  button:active {
    transform: translateY(1px);
  }
`;

/* Right column (comments) */
const RightSection = styled.aside`
  background: rgba(255, 255, 255, 0.02);
  padding: 0;
  border-radius: 12px;
  color: #fff;
  height: 85vh;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(155, 92, 255, 0.06);
  overflow: hidden;

  @media (max-width: 900px) {
    height: auto;
    margin-top: 16px;
  }
`;

const SuggestionsHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: #c59fff;
  font-weight: 700;
`;

const SuggestionsList = styled.div`
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const SuggestionItem = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.01);
  border: 1px solid rgba(255, 255, 255, 0.02);
  align-items: flex-start;
`;

const SuggestInfo = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    color: #fff;
    font-size: 13px;
  }

  small {
    color: #aab1c7;
    font-size: 12px;
  }
`;

const SuggestionText = styled.div`
  margin-top: 6px;
  color: #d6d6df;
  font-size: 14px;
  word-break: break-word;
`;

const SuggestionInputBox = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.03);
  align-items: flex-start;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.02), transparent);
`;

const Input = styled.textarea`
  flex: 1;
  min-height: 56px;
  padding: 10px;
  border-radius: 10px;
  background: #0f0f10;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.03);
  resize: none;
  font-size: 14px;
`;

const Button = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  border: none;
  color: #fff;
  padding: 10px 14px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  min-width: 92px;

  &:disabled {
    background: #444;
    cursor: not-allowed;
  }
`;

/* popup/confirmation modal */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: #0f0f10;
  padding: 18px;
  border-radius: 12px;
  width: 92%;
  max-width: 420px;
  color: #fff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

const CancelButton = styled.button`
  background: #222;
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
`;

const DeleteButton = styled.button`
  background: #ef4444;
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
`;

/* small helpers */
const Small = styled.span`
  font-size: 12px;
  color: #9aa0b3;
`;
export const AIModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AIModalContent = styled.div`
  background: #111;
  color: #fff;
  width: 92%;
  max-width: 760px;
  border-radius: 12px;
  padding: 20px;
  max-height: 88vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
`;

const AIModal = styled.div`
  background: #0d0d0e;
  color: #e6e6e6;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 8px 40px rgba(155, 92, 255, 0.12);
`;

/* --------------------------------------------------------------------------
   Component logic (all original features preserved)
   -------------------------------------------------------------------------- */

const SingleIssuePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext) || {};
  const [issue, setIssue] = useState(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [activeAISolution, setActiveAISolution] = useState(null);
  const [activeIssue, setActiveIssue] = useState(null);
  const [loadingAISolution, setLoadingAISolution] = useState(false);
  // suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [suggestText, setSuggestText] = useState("");
  const [addingSuggestion, setAddingSuggestion] = useState(false);

  // menus/modals
  const [openMenu, setOpenMenu] = useState(null);
  const [openCommentMenu, setOpenCommentMenu] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showSolvedModal, setShowSolvedModal] = useState(false);
  const [solvedId, setSolvedId] = useState(null);

  // likes
  const [likes, setLikes] = useState({});
  const [userLikes, setUserLikes] = useState({});

  // code view
  const [showFullCode, setShowFullCode] = useState(null);

  const suggestionsRef = useRef(null);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);
  /* ------------------- fetch issue ------------------- */
  useEffect(() => {
    fetchIssue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchIssue = async () => {
    try {
      const docRef = doc(db, "codeIssues", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setIssue({ id: docSnap.id, ...docSnap.data() });
      } else {
        console.warn("No such issue:", id);
        setIssue(null);
      }
    } catch (err) {
      console.error("Error fetching issue:", err);
    }
  };

  /* ------------------- realtime suggestions ------------------- */
  useEffect(() => {
    if (!id) return;
    const suggestRef = collection(db, "codeIssues", id, "suggestions");
    const suggestQuery = query(suggestRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(suggestQuery, (snapshot) => {
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSuggestions(list);
    });

    return () => unsubscribe();
  }, [id]);

  /* ------------------- likes fetch & toggle ------------------- */
  const fetchLikes = async (issueId) => {
    try {
      const likesRef = collection(db, "codeIssues", issueId, "likes");
      const snap = await getDocs(likesRef);
      setLikes((prev) => ({ ...prev, [issueId]: snap.size }));

      if (user?.id) {
        const q = query(likesRef, where("userId", "==", user.id));
        const likedSnap = await getDocs(q);
        setUserLikes((prev) => ({ ...prev, [issueId]: !likedSnap.empty }));
      }
    } catch (err) {
      console.error("Error fetching likes for", issueId, err);
    }
  };

  useEffect(() => {
    if (issue?.id) fetchLikes(issue.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue, user?.id]);

  const updateIssueInState = (idToUpdate, updates) => {
    if (!issue) return;
    if (issue.id === idToUpdate) {
      setIssue((prev) => ({ ...prev, ...updates }));
    }
  };

  const toggleLike = async (issueId) => {
    if (!user?.id) {
      alert("Please login to like");
      return;
    }

    try {
      const likesRefColl = collection(db, "codeIssues", issueId, "likes");
      const issueRef = doc(db, "codeIssues", issueId);

      if (userLikes[issueId]) {
        // remove user's like doc
        const q = query(likesRefColl, where("userId", "==", user.id));
        const snap = await getDocs(q);
        snap.forEach(async (d) => {
          await deleteDoc(doc(db, "codeIssues", issueId, "likes", d.id));
        });

        setUserLikes((prev) => ({ ...prev, [issueId]: false }));
        setLikes((prev) => ({ ...prev, [issueId]: (prev[issueId] || 1) - 1 }));
        await updateDoc(issueRef, { likesCount: increment(-1) });
        updateIssueInState(issueId, { likesCount: (likes[issueId] || 1) - 1 });
      } else {
        await addDoc(likesRefColl, {
          userId: user.id,
          createdAt: serverTimestamp(),
        });

        setUserLikes((prev) => ({ ...prev, [issueId]: true }));
        setLikes((prev) => ({ ...prev, [issueId]: (prev[issueId] || 0) + 1 }));
        await updateDoc(issueRef, { likesCount: increment(1) });
        updateIssueInState(issueId, { likesCount: (likes[issueId] || 0) + 1 });
      }
    } catch (err) {
      console.error("Error toggling like:", err);
    }
  };

  /* ------------------- suggestions submit ------------------- */
  const handleSuggestSubmit = async (issueId) => {
    if (!suggestText.trim()) return;
    if (!user?.id) {
      alert("Please login to submit suggestion.");
      return;
    }

    try {
      setAddingSuggestion(true);
      const suggestRef = collection(db, "codeIssues", issueId, "suggestions");
      await addDoc(suggestRef, {
        text: suggestText.trim(),
        userId: user.id,
        userName: user.name || "Anonymous",
        userImage: user.profilePic || "",
        createdAt: serverTimestamp(),
        issueId,
      });

      const issueRef = doc(db, "codeIssues", issueId);
      await updateDoc(issueRef, { suggestionCount: increment(1) });

      // local UI update for counts
      updateIssueInState(issueId, {
        suggestionCount: issue?.suggestionCount ? issue.suggestionCount + 1 : 1,
      });

      setSuggestText("");
    } catch (err) {
      console.error("Error adding suggestion:", err);
      alert("Unable to submit suggestion right now.");
    } finally {
      setAddingSuggestion(false);
    }
  };

  const openAISolutionModal = async (issue) => {
    try {
      setActiveIssue(issue);
      setAiModalOpen(true);
      setLoadingAISolution(true);

      const solRef = collection(db, "codeIssues", issue.id, "solutions");
      const solSnap = await getDocs(solRef);
      const aiDoc = solSnap.docs.find((d) => d.data().type === "ai");

      if (aiDoc) {
        setActiveAISolution(aiDoc.data().content);
      } else {
        setActiveAISolution("No AI solution found for this issue yet.");
      }
    } catch (err) {
      console.error("Error fetching AI solution:", err);
      setActiveAISolution("Failed to fetch AI solution.");
    } finally {
      setLoadingAISolution(false);
    }
  };
  /* ------------------- chat room ensure ------------------- */
  const ensureChatRoomAndGo = async (issueObj) => {
    if (!user?.id) {
      alert("Login required to chat");
      return;
    }

    try {
      const roomRef = doc(db, "chatRooms", issueObj.id);
      const roomSnap = await getDoc(roomRef);

      if (!roomSnap.exists()) {
        await setDoc(roomRef, {
          issueId: issueObj.id,
          issueTitle: issueObj.title || "Issue",
          createdBy: issueObj.postedBy,
          participants: [issueObj.posterId, user.id].filter(Boolean),
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
        });
      } else {
        await updateDoc(roomRef, {
          participants: arrayUnion(user.id),
          lastMessageAt: serverTimestamp(),
        });
      }

      navigate(`/chats`);
    } catch (err) {
      console.error("Failed to open chat room:", err);
      alert("Unable to open chat right now.");
    }
  };

  /* ------------------- edit / delete / mark solved ------------------- */
  const handleEdit = (issueId) => {
    navigate(`/edit-code-issue/${issueId}`);
  };

  const handleDelete = (issueId) => {
    setDeleteId(issueId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteDoc(doc(db, "codeIssues", deleteId));
      setShowDeleteModal(false);
      navigate("/code-issues");
    } catch (err) {
      console.error("Error deleting issue:", err);
      alert("Unable to delete now.");
    } finally {
      setDeleteId(null);
    }
  };

  const handleMarkSolved = (issueId) => {
    setOpenMenu(false);
    setSolvedId(issueId);
    setShowSolvedModal(true);
  };

  const confirmSolved = async () => {
    if (!solvedId) return;
    try {
      const issueRef = doc(db, "codeIssues", solvedId);
      await updateDoc(issueRef, {
        status: "SOLVED",
        solvedAt: serverTimestamp(),
      });
      setShowSolvedModal(false);
      fetchIssue();
    } catch (err) {
      console.error("Error marking solved:", err);
      alert("Unable to update status.");
    } finally {
      setSolvedId(null);
    }
  };

  /* ------------------- comment actions (edit/delete) - placeholders ------------------- */
  const handleCommentDelete = async (suggestionId) => {
    try {
      await deleteDoc(doc(db, "codeIssues", id, "suggestions", suggestionId));
    } catch (err) {
      console.error("Error deleting suggestion:", err);
    }
  };

  /* --------------------------------------------------------------------------
     Render
     -------------------------------------------------------------------------- */

  if (!issue) {
    return (
      <Container>
        <p style={{ color: "#cfcfd6" }}>Loading issue...</p>
      </Container>
    );
  }

  return (
    <Container>
      <LayoutGrid>
        {/* LEFT: Issue */}
        <IssueCard>
          <CardTop>
            <CardHeader>
              <AvatarWrapper>
                {issue.userImage ? (
                  <AvatarImage src={issue.userImage} alt={issue.postedBy} />
                ) : (
                  <AvatarFallback>
                    <User color="#fff" />
                  </AvatarFallback>
                )}
              </AvatarWrapper>

              <UserInfo>
                <span>{issue.postedBy || "Anonymous"}</span>
                {issue.createdAt?.seconds ? (
                  <span>
                    {new Date(
                      issue.createdAt.seconds * 1000
                    ).toLocaleDateString()}{" "}
                    •{" "}
                    {new Date(
                      issue.createdAt.seconds * 1000
                    ).toLocaleTimeString()}
                  </span>
                ) : (
                  <Small>Just now</Small>
                )}
              </UserInfo>
            </CardHeader>

            {/* poster menu */}
            {user?.id === issue.posterId && (
              <div
                style={{ position: "relative" }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setOpenMenu(openMenu === issue.id ? null : issue.id);
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#cfcfe6",
                    padding: 6,
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  <MoreVertical color="white" />
                </button>
                {openMenu === issue.id && (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 36,
                      background: "#111",
                      border: "1px solid rgba(255,255,255,0.04)",
                      borderRadius: 8,
                      overflow: "hidden",
                      zIndex: 50,
                    }}
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    <button
                      onClick={() => handleEdit(issue.id)}
                      style={{
                        display: "flex",
                        gap: 8,
                        padding: 10,
                        background: "transparent",
                        border: "none",
                        width: "100%",
                        color: "#fff",
                        alignItems: "center",
                      }}
                    >
                      <Edit size={16} /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(issue.id)}
                      style={{
                        display: "flex",
                        gap: 8,
                        padding: 10,
                        background: "transparent",
                        border: "none",
                        width: "100%",
                        color: "#ff7a7a",
                        alignItems: "center",
                      }}
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                    {issue.status === "UNSOLVED" && (
                      <button
                        onClick={() => handleMarkSolved(issue.id)}
                        style={{
                          display: "flex",
                          gap: 8,
                          padding: 10,
                          background: "transparent",
                          border: "none",
                          width: "100%",
                          color: "#aaffc9",
                          alignItems: "center",
                        }}
                      >
                        <CheckCircle2 size={16} /> Mark as Solved
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardTop>

          <div>
            <h3>{issue.title}</h3>
            <p style={{ marginTop: 8 }}>{issue.description}</p>

            {issue.screenshot && (
              <Screenshot src={issue.screenshot} alt="screenshot" />
            )}

            {issue.code && (
              <>
                <CodeBlock>
                  {showFullCode === issue.id
                    ? issue.code
                    : issue.code.split("\n").slice(0, 6).join("\n") +
                      (issue.code.split("\n").length > 6 ? "\n..." : "")}
                </CodeBlock>

                {issue.code.split("\n").length > 6 && (
                  <ViewMore
                    onClick={() =>
                      setShowFullCode(
                        showFullCode === issue.id ? null : issue.id
                      )
                    }
                  >
                    {showFullCode === issue.id ? "Hide code" : "View full code"}
                  </ViewMore>
                )}
              </>
            )}
          </div>

          <Meta>
            <Tag variant="type">{issue.type || "Free"}</Tag>
            <Tag variant="tech">{issue.technology || "Unknown"}</Tag>
            {issue.difficulty && (
              <Tag variant="difficult">{issue.difficulty}</Tag>
            )}
            <Tag variant="status">
              {(issue.status || "UNSOLVED").toLowerCase()}
            </Tag>

            <Small style={{ marginLeft: "auto", alignSelf: "center" }}>
              {issue.reward ? `${issue.reward} coins` : ""}
            </Small>
          </Meta>

          <Actions>
            <button
              onClick={() => {
                if (!user?.id) {
                  alert("Please login to like");
                  return;
                }
                toggleLike(issue.id);
              }}
            >
              <ThumbsUp
                color={userLikes[issue.id] ? "#60A5FA" : undefined}
                size={16}
              />
              {likes[issue.id] ?? issue.likesCount ?? 0}
            </button>

            <button
              onClick={() => {
                // open suggestion input area (scroll to right section)
                const el = document.getElementById("suggest-input");
                if (el)
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
            >
              <Lightbulb size={16} /> {issue.suggestionCount || 0}
            </button>

            <button
              onClick={() => {
                ensureChatRoomAndGo(issue);
              }}
            >
              <MessageSquare size={16} /> Chat
            </button>

            <button
              onClick={() => {
                // open AI solutions if present (read from subcollection 'solutions')
                // simple fetch to show CPU icon action (non-blocking)
                openAISolutionModal(issue);
              }}
            >
              <Cpu size={16} /> AI Solution
            </button>
          </Actions>
        </IssueCard>

        {/* RIGHT: Suggestions / Comments */}
        <RightSection>
          <SuggestionsHeader>Comments ({suggestions.length})</SuggestionsHeader>

          <SuggestionsList ref={suggestionsRef}>
            {suggestions && suggestions.length > 0 ? (
              suggestions.map((s) => (
                <SuggestionItem key={s.id}>
                  <AvatarWrapper style={{ width: 40, height: 40 }}>
                    {s.userImage ? (
                      <AvatarImage src={s.userImage} alt={s.userName} />
                    ) : (
                      <AvatarFallback>
                        <User color="#fff" />
                      </AvatarFallback>
                    )}
                  </AvatarWrapper>

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <div>
                        <strong style={{ color: "#fff" }}>
                          {s.userName || "Anonymous"}
                        </strong>
                        <div style={{ fontSize: 12, color: "#aab1c7" }}>
                          {getTimeAgo(s.createdAt)}
                        </div>
                      </div>

                      {user?.id === s.userId && (
                        <div style={{ marginLeft: 8 }}>
                          <button
                            onClick={() =>
                              setOpenCommentMenu(
                                openCommentMenu === s.id ? null : s.id
                              )
                            }
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "#ccc",
                              padding: 6,
                              cursor: "pointer",
                            }}
                          >
                            <MoreVertical />
                          </button>

                          {openCommentMenu === s.id && (
                            <div
                              style={{
                                position: "absolute",
                                right: 30,
                                marginTop: 6,
                                background: "#111",
                                border: "1px solid rgba(255,255,255,0.03)",
                                borderRadius: 8,
                                overflow: "hidden",
                                zIndex: 50,
                              }}
                            >
                              <button
                                onClick={() => alert("Edit comment - TODO")}
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  padding: 10,
                                  background: "transparent",
                                  border: "none",
                                  color: "#fff",
                                  width: "100%",
                                  textAlign: "left",
                                  cursor: "pointer",
                                }}
                              >
                                <Edit size={14} /> Edit
                              </button>
                              <button
                                onClick={() => handleCommentDelete(s.id)}
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  padding: 10,
                                  background: "transparent",
                                  border: "none",
                                  color: "#ff7a7a",
                                  width: "100%",
                                  textAlign: "left",
                                  cursor: "pointer",
                                }}
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <SuggestionText>{s.text}</SuggestionText>
                  </div>
                </SuggestionItem>
              ))
            ) : (
              <div
                style={{ textAlign: "center", color: "#9aa0b3", padding: 16 }}
              >
                No comments yet. Be the first!
              </div>
            )}
          </SuggestionsList>

          <div id="suggest-input">
            <SuggestionInputBox>
              <AvatarWrapper style={{ width: 44, height: 44 }}>
                {user?.profilePic ? (
                  <AvatarImage src={user.profilePic} alt={user?.name} />
                ) : (
                  <AvatarFallback>
                    <User color="#fff" />
                  </AvatarFallback>
                )}
              </AvatarWrapper>

              <Input
                placeholder="Write your suggestion or a quick hint..."
                value={suggestText}
                onChange={(e) => setSuggestText(e.target.value)}
              />

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Button
                  onClick={() => handleSuggestSubmit(issue.id)}
                  disabled={!suggestText.trim() || addingSuggestion}
                >
                  {addingSuggestion ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    "Send"
                  )}
                </Button>

                {/* <button
                  onClick={() => {
                    if (!user) {
                      alert("Login to post");
                      return;
                    }
                    setSuggestText("");
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#aab1c7",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Cancel
                </button> */}
              </div>
            </SuggestionInputBox>
          </div>
        </RightSection>
      </LayoutGrid>

      {/* Delete confirm modal */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <h3 style={{ color: "#c59fff" }}>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this issue? This action cannot be
              undone.
            </p>
            <ModalActions>
              <CancelButton onClick={() => setShowDeleteModal(false)}>
                Cancel
              </CancelButton>
              <DeleteButton onClick={confirmDelete}>Delete</DeleteButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Solved confirm modal */}
      {showSolvedModal && (
        <ModalOverlay>
          <ModalContent>
            <h3 style={{ color: "#c59fff" }}>Mark as Solved</h3>
            <p>
              Mark this issue as solved? This will update its status for all
              users.
            </p>
            <ModalActions>
              <CancelButton onClick={() => setShowSolvedModal(false)}>
                Cancel
              </CancelButton>
              <Button onClick={confirmSolved}>Confirm</Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
      {aiModalOpen && (
        <AIModalOverlay onClick={() => setAiModalOpen(false)}>
          <AIModalContent onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div>
                <h2 style={{ margin: 0, color: "#c59fff" }}>🤖 AI Solution</h2>
                <Small style={{ marginTop: 6 }}>{activeIssue?.title}</Small>
              </div>
              <button
                onClick={() => setAiModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#cfcfe6",
                  cursor: "pointer",
                }}
              >
                <X />
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              {loadingAISolution ? (
                <div style={{ textAlign: "center", padding: 20 }}>
                  <Loader2 className="animate-spin" />
                  <div style={{ color: "#c59fff", marginTop: 8 }}>
                    Loading AI solution...
                  </div>
                </div>
              ) : (
                <AIModal>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || "");
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={oneDark}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, "")}
                          </SyntaxHighlighter>
                        ) : (
                          <code
                            style={{
                              background: "#111",
                              padding: "0.2rem 0.4rem",
                              borderRadius: 4,
                              color: "#c59fff",
                            }}
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      },
                      h1: ({ node, ...props }) => (
                        <h1 style={{ color: "#c59fff" }} {...props} />
                      ),
                      h2: ({ node, ...props }) => (
                        <h2 style={{ color: "#c59fff" }} {...props} />
                      ),
                      p: ({ node, ...props }) => (
                        <p style={{ color: "#d6d6df" }} {...props} />
                      ),
                      li: ({ node, ...props }) => (
                        <li style={{ color: "#d6d6df" }} {...props} />
                      ),
                    }}
                  >
                    {activeAISolution || "No AI solution available."}
                  </ReactMarkdown>
                </AIModal>
              )}
            </div>
          </AIModalContent>
        </AIModalOverlay>
      )}
    </Container>
  );
};

export default SingleIssuePage;
